import { FC, useCallback, useMemo } from "react";
import styled from "@emotion/styled";
import { SessionPartProps } from "@/mds/components/sessions/session-parts/session-part.types";
import {
  ClientStatusSessionPartState,
  ClientStatusSessionErrorPart,
  ClientStatusSessionPart,
} from "@/mds/components/sessions/session.types";
import { MDSBubble } from "@/mds/components/bubble/bubble";
import {
  MDSBubbleAlignment,
  MDSBubbleVariant,
  MDSBubbleSlotName,
} from "@/mds/components/bubble/bubble.types";
import { MDSBubbleSessionSenderIcon } from "@/mds/components/sessions/session-sender-icon";
import { MDSSessionPartContainer } from "@/mds/components/sessions/session-parts/session-part.style";
import {
  useIsSessionStatusFirstPartInSessionGroup,
  useIsSessionStatusLastPartInSessionGroup,
} from "@/mds/components/sessions/session-context/mds-session-context.hooks";
import { AnimatePresence, motion } from "motion/react";
import { useMDSSession } from "@/mds/components/sessions/session-context/mds-session-context";
import { MDSSessionErrorKind } from "@/mds/components/sessions/session-context";
import { MDSLink } from "@/mds/components/text";
import { MDSLoadingArrows, MDSLoadingArrowsSize } from "@/mds/components/loading/loading-arrows";
import { MDSText, MDSTextColor, MDSTextVariant } from "@/mds/components/text";
import { SESSION_THOUGHT_PROCESS_TEXTS } from "@/mds/components/sessions/session-item";

type Props = SessionPartProps<ClientStatusSessionPart>;

export const MDSSessionClientStatusPart: FC<Props> = ({ part }) => {
  const isFirstInGroup = useIsSessionStatusFirstPartInSessionGroup({ part });
  const isLastInGroup = useIsSessionStatusLastPartInSessionGroup({ part });

  const renderContent = useMemo(() => {
    switch (part.state) {
      case ClientStatusSessionPartState.LoadingIndicator:
        return (
          <>
            {isFirstInGroup && (
              <LoadingContainer>
                <MDSLoadingArrows size={MDSLoadingArrowsSize.XSmall} loop />
                <MDSText
                  variant={MDSTextVariant.TextXs}
                  color={MDSTextColor.Tertiary}
                  style={{ height: "24px" }}
                >
                  {SESSION_THOUGHT_PROCESS_TEXTS.THINKING}
                </MDSText>
              </LoadingContainer>
            )}
          </>
        );
      case ClientStatusSessionPartState.ErrorMessage:
        return (
          <>
            <MDSBubbleSessionSenderIcon slot={MDSBubbleSlotName.Icon} isVisible={isFirstInGroup} />
            <ErrorPart part={part} isFirstInGroup={isFirstInGroup} />
          </>
        );
      case ClientStatusSessionPartState.Placeholder:
        /** Placeholder for spacing. */
        return (
          <>
            <MDSBubbleSessionSenderIcon slot={MDSBubbleSlotName.Icon} isVisible={false} />
          </>
        );
      default:
        return null;
    }
  }, [part, isFirstInGroup]);

  const isPlaceholder = useMemo(() => {
    return part.state === ClientStatusSessionPartState.Placeholder;
  }, [part.state]);

  return (
    <Wrapper isPlaceholder={isPlaceholder} isFirstInGroup={isFirstInGroup}>
      <AnimatePresence initial={false} mode="wait">
        <StyledWrapper
          key={part.state}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          <Bubble
            variant={MDSBubbleVariant.Transparent}
            align={MDSBubbleAlignment.Left}
            isLastInGroup={isLastInGroup}
          >
            <BubbleContent>{renderContent}</BubbleContent>
          </Bubble>
        </StyledWrapper>
      </AnimatePresence>
    </Wrapper>
  );
};

const StyledWrapper = styled(motion.div)`
  width: 100%;
  height: 100%;
`;

const BubbleContent = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const ErrorPart: FC<{ part: ClientStatusSessionErrorPart; isFirstInGroup: boolean }> = ({
  part,
  isFirstInGroup,
}) => {
  const { message, canRetry, kind } = part.data;
  const { clearSessionError, handleRetry } = useMDSSession();

  const handleRetryCb = useCallback(() => {
    if (canRetry) {
      clearSessionError();
      handleRetry?.();
    }
  }, [canRetry, clearSessionError, handleRetry]);

  if (kind === MDSSessionErrorKind.SessionEventError) {
    return (
      <StyledErrorActions isFirstInGroup={isFirstInGroup}>
        <StyledErrorMessage>
          {message} {canRetry && <StyledRetryLink onClick={handleRetryCb}>Retry?</StyledRetryLink>}
        </StyledErrorMessage>
      </StyledErrorActions>
    );
  }

  return null;
};

const Bubble = styled(MDSBubble)`
  width: 100%;
`;

const Wrapper = styled(MDSSessionPartContainer)<{
  isPlaceholder: boolean;
  isFirstInGroup: boolean;
}>`
  min-width: ${({ theme }) => theme.spacing["4"]};
  min-height: 44px;
  margin-bottom: ${({ isFirstInGroup }) => (isFirstInGroup ? 0 : "-8px")};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 24px;
  overflow: hidden;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

export const StyledErrorActions = styled.div<{ isFirstInGroup: boolean }>`
  display: flex;
  gap: ${({ theme }) => theme.spacing["2"]};
  justify-content: start;
  align-items: end;
  font-size: ${({ theme }) => theme.fontSizes["paragraph-sm"]};
  color: ${({ theme }) => theme.colors.text.danger};
  margin-top: ${({ isFirstInGroup, theme }) => (isFirstInGroup ? theme.spacing["2"] : 0)};
`;

export const StyledRetryLink = styled(MDSLink)`
  color: ${({ theme }) => theme.colors.text.emphasis};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  text-decoration: none;
`;

export const StyledErrorMessage = styled.div`
  font-size: ${({ theme }) => theme.fontSizes["paragraph-sm"]};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.text.danger};
  line-height: ${({ theme }) => theme.lineHeights["paragraph-sm"]};
`;
